// @flow
import React, { Component } from 'react';
import Chartist from 'chartist';
import ChartistGraph from 'react-chartist';

import { PrimaryButton } from '../../../Buttons.jsx';

import ageLength from './constants/statage';

type Props = {
  measurements: Object[],
  sex: string,
}

type State = {
  unit: string,
}

class ChildLengthAgeChart extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      unit: 'cm',
    };
  }

  toggleUnits = () => {
    this.setState({
      unit: (this.state.unit === 'in' ? 'cm' : 'in'),
    });
  }

  // needed to re-render based on unit properly for Chartist plugin
  getYAxis = () => `Stature (${this.state.unit})`;

  render() {
    const CM_TO_IN = 2.54;
    const { unit } = this.state;
    const measurements = this.props.measurements.slice()
      // eslint-disable-next-line no-restricted-globals
      .filter(x => !isNaN(x.age))
      .sort((a, b) => a.age - b.age)
      .map((item) => {
        const x = item.age;
        const y = (unit === 'cm' ? item.length : (item.length / CM_TO_IN).toFixed(1));
        const percentile = item.lengthPercentile;
        return {
          value: { x, y },
          // string for popover text
          meta: `Age: ${item.ageString}, Stature: ${y} ${unit} (${percentile}%)`,
        };
      });

    const {
      p3, p10, p25, p50, p75, p90, p97,
    } = ageLength[this.props.sex];
    const percentiles = [p3, p10, p25, p50, p75, p90, p97];
    const labels = ['3', '10', '25', '50', '75', '90', '97'];
    for (let i = 0; i < percentiles.length; i++) {
      percentiles[i] = percentiles[i].map((val, index) => ({
        x: (index === 0 ? 24 : index + 23.5),
        y: (unit === 'cm' ? val : val / CM_TO_IN),
        label: labels[i],
      }));
    }
    // plot single points at end of line to have labels for percentile lines
    const percentileLabels = percentiles.map(p => ({
      name: 'percentileLabels',
      data: [p[p.length - 1]],
    }));

    const data = {
      series: [...percentiles, ...percentileLabels,
        {
          name: 'measurements',
          data: measurements,
        },
      ],
    };

    const options = {
      series: {
        measurements: {
          showPoint: true,
          lineSmooth: Chartist.Interpolation.none({
            fillHoles: true,
          }),
        },
        percentileLabels: {
          showPoint: true,
        },
      },
      axisX: {
        labelOffset: {
          x: -7,
          y: 0,
        },
        type: Chartist.FixedScaleAxis,
        high: 243,
        low: 24,
        divisor: 73,
        labelInterpolationFnc: value => (value % 12 === 0 ? value / 12 : ''),
      },
      axisY: {
        labelOffset: {
          x: 0,
          y: 7,
        },
        type: Chartist.FixedScaleAxis,
        high: (unit === 'cm' ? 200 : 80),
        low: (unit === 'cm' ? 70 : 26),
        divisor: (unit === 'cm' ? 65 : 27),
        // don't interpolate if unit is inches (display each line), else every 5
        labelInterpolationFnc: value => (value % 10 === 0 || unit === 'in' ? value : ''),
      },
      showPoint: false,
      height: '85vh',
      chartPadding: {
        bottom: 10,
        left: 10,
      },
      plugins: [
        Chartist.plugins.ctAxisTitle({
          axisX: {
            axisTitle: 'Age (years)',
            offset: {
              x: 0,
              y: 35,
            },
          },
          axisY: {
            axisTitle: this.getYAxis,
            offset: {
              x: 0,
              y: 10,
            },
            flipTitle: true,
          },
        }),
        Chartist.plugins.tooltip(),
        Chartist.plugins.ctPointLabels({
          textAnchor: 'start',
          labelOffset: {
            x: 3,
            y: 4,
          },
        }),
      ],
    };

    return (
      <div>
        <PrimaryButton onClick={this.toggleUnits}>Toggle Units</PrimaryButton>
        <ChartistGraph data={data} options={options} type={'Line'} />
      </div>
    );
  }
}

export default ChildLengthAgeChart;



// WEBPACK FOOTER //
// ./client/src/components/patientPages/growthChart/child/ChildLengthAgeChart.jsx